import { useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { dayjs } from "@/utils/date";
import { useTables } from "@/hooks/use-tables";
import { useZones } from "@/hooks/use-zones";
import {
  useCreateTableMaintenance,
  useDeleteTableMaintenance,
  useTableMaintenance,
} from "@/hooks/use-table-maintenance";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { ApiTableMaintenanceWindow } from "@/types/api";
import type { Table, ZoneConfig } from "@/store/types";

function formatDate(value: string): string {
  return dayjs(value)
    .format("ddd D [de] MMM YYYY")
    .replace(/^\w/, (c) => c.toUpperCase());
}

const EMPTY = { table_id: "", date: "", end_date: "", reason: "" };

export function TableMaintenancePanel() {
  const { data: windows, isPending } = useTableMaintenance();
  const { data: tables } = useTables();
  const { data: zones } = useZones();
  const createMut = useCreateTableMaintenance();
  const deleteMut = useDeleteTableMaintenance();

  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY });
  const [confirmDelete, setConfirmDelete] = useState<ApiTableMaintenanceWindow | null>(null);

  const set = (patch: Partial<typeof draft>) => setDraft((d) => ({ ...d, ...patch }));

  // Table id → "Zona · Mesa" label for both the picker and the list.
  const tableLabels = useMemo(() => {
    const zoneName = new Map((zones ?? []).map((z: ZoneConfig) => [z.id, z.name]));
    const map = new Map<string, string>();
    for (const t of (tables ?? []) as Table[]) {
      map.set(t.id, `${zoneName.get(t.zoneId) ?? "Zona"} · ${t.name}`);
    }
    return map;
  }, [tables, zones]);

  const cancel = () => {
    setAdding(false);
    setDraft({ ...EMPTY });
  };

  const canSubmit =
    !!draft.table_id && !!draft.date && (!draft.end_date || draft.end_date >= draft.date);

  const submit = () => {
    if (!canSubmit) return;
    createMut.mutate(
      {
        table_id: draft.table_id,
        date: draft.date,
        end_date: draft.end_date || null,
        reason: draft.reason.trim(),
      },
      { onSuccess: cancel },
    );
  };

  if (isPending) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="size-6 text-text-secondary" />
      </div>
    );
  }

  const sorted = [...(windows ?? [])].toSorted((a, b) => a.date.localeCompare(b.date));
  const orderedTables = [...((tables ?? []) as Table[])].toSorted((a, b) =>
    (tableLabels.get(a.id) ?? "").localeCompare(tableLabels.get(b.id) ?? ""),
  );

  return (
    <section className="flex flex-col gap-4 max-w-2xl">
      <header>
        <h2 className="text-xl font-semibold tracking-tight">Mesas en mantenimiento</h2>
        <p className="text-sm text-text-secondary mt-1">
          Saca una mesa de servicio por un rango de fechas (reparación, daño). Mientras dure, la
          mesa no aparece para nadie — ni web, ni staff, ni walk-ins — y vuelve sola cuando pasa la
          fecha final. Las reservas ya creadas en esa mesa no se cancelan.
        </p>
      </header>

      <div className="flex flex-col gap-2">
        {sorted.map((w) => {
          const end = w.end_date && w.end_date !== w.date ? ` → ${formatDate(w.end_date)}` : "";
          return (
            <div
              key={w.id}
              className="flex items-start gap-3 rounded-xl border border-border bg-white p-3"
            >
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">{tableLabels.get(w.table_id) ?? "Mesa"}</div>
                <div className="text-[13px] text-text-muted">
                  {formatDate(w.date)}
                  {end}
                </div>
                {w.reason && <div className="text-[12px] text-text-muted mt-0.5">{w.reason}</div>}
              </div>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Quitar mantenimiento"
                onClick={() => setConfirmDelete(w)}
                className="shrink-0 text-red-500 hover:bg-red-50"
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          );
        })}
        {sorted.length === 0 && !adding && (
          <p className="text-sm text-text-muted py-2">No hay mesas en mantenimiento.</p>
        )}
      </div>

      {adding ? (
        <div className="rounded-xl border border-dashed border-border bg-muted/20 p-4 flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Label>Mesa</Label>
            <select
              value={draft.table_id}
              onChange={(e) => set({ table_id: e.target.value })}
              className="h-10 rounded-md border border-border bg-white px-3 text-sm"
            >
              <option value="">Elegí una mesa…</option>
              {orderedTables.map((t) => (
                <option key={t.id} value={t.id}>
                  {tableLabels.get(t.id)}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <Label>Desde</Label>
              <Input
                type="date"
                value={draft.date}
                onChange={(e) => set({ date: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label>Hasta (opcional)</Label>
              <Input
                type="date"
                value={draft.end_date}
                min={draft.date || undefined}
                onChange={(e) => set({ end_date: e.target.value })}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <Label>Motivo (opcional)</Label>
            <Input
              value={draft.reason}
              onChange={(e) => set({ reason: e.target.value })}
              placeholder="Ej. Reparación de pata"
            />
          </div>
          <div className="flex gap-2 mt-1">
            <Button
              onClick={submit}
              disabled={!canSubmit || createMut.isPending}
              className="flex-1"
            >
              {createMut.isPending ? (
                <>
                  <Spinner className="mr-2 size-4" /> Guardando…
                </>
              ) : (
                "Guardar"
              )}
            </Button>
            <Button variant="ghost" onClick={cancel}>
              Cancelar
            </Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" onClick={() => setAdding(true)} className="self-start">
          <Plus className="size-4" /> Poner mesa en mantenimiento
        </Button>
      )}

      <AlertDialog
        open={confirmDelete !== null}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Devolver la mesa a servicio?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete && (
                <>
                  Se quita el mantenimiento de{" "}
                  <span className="font-medium text-foreground">
                    {tableLabels.get(confirmDelete.table_id) ?? "la mesa"}
                  </span>
                  . Volverá a estar disponible.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmDelete) {
                  deleteMut.mutate(confirmDelete.id);
                  setConfirmDelete(null);
                }
              }}
            >
              Devolver a servicio
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

export default TableMaintenancePanel;
